import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { getPlan } from "../../shared/plans";
import { IS_STATIC_BUILD } from "../lib/api";
import { logout, usePlatform, useSession } from "../lib/session";
import { navigate, type Route } from "../lib/useHashRoute";
import { IconAccount, IconBell, IconCard, IconKey, IconLogout, IconShield, IconTable } from "./Icons";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return `${first}${last}`.toUpperCase() || "?";
}

/** Caminho atual, para voltar a ele depois de entrar. */
function currentPath(): string {
  return `${window.location.pathname}${window.location.search}`;
}

/**
 * Canto direito do cabeçalho: "Entrar" para o visitante, e o menu da conta para quem
 * está logado. Enquanto a sessão carrega, reserva o espaço do botão para o cabeçalho
 * não pular quando a resposta chega.
 */
export function AccountMenu({ page }: { page: Route["page"] }) {
  const session = useSession();
  const platform = usePlatform();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    // Foco no primeiro item: quem abriu pelo teclado continua pelo teclado.
    wrapRef.current?.querySelector<HTMLButtonElement>("[role=menuitem]")?.focus();
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Build estática ou servidor com as contas desligadas: o cabeçalho fica como era.
  if (IS_STATIC_BUILD || platform?.contas === false) return null;

  if (session.status === "loading") return <span className="account-slot" aria-hidden />;

  if (session.status === "anon") {
    if (page === "login" || page === "signup") return null;
    return (
      <button
        className="btn btn-secondary btn-sm account-enter"
        onClick={() => navigate({ page: "login", volta: page === "home" || page === "landing" ? undefined : currentPath() })}
      >
        <IconAccount size={15} />
        <span className="account-enter-label">Entrar</span>
      </button>
    );
  }

  const { usuario, plano, limites } = session.payload;
  const go = (route: Route) => {
    setOpen(false);
    navigate(route);
  };

  const onMenuKey = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const items = Array.from(wrapRef.current?.querySelectorAll<HTMLButtonElement>("[role=menuitem]") ?? []);
    const index = items.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === "ArrowDown" ? (index + 1) % items.length : (index - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  return (
    <div className="account-menu" ref={wrapRef}>
      <button
        ref={buttonRef}
        className={`account-avatar${open ? " open" : ""}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Conta de ${usuario.nome}`}
        title={usuario.nome}
        onClick={() => setOpen((value) => !value)}
      >
        {initials(usuario.nome)}
      </button>
      {open && (
        <div className="account-dropdown" role="menu" aria-label="Minha conta" onKeyDown={onMenuKey}>
          <div className="account-dropdown-head">
            <span className="account-dropdown-name">{usuario.nome}</span>
            <span className="account-dropdown-email">{usuario.email}</span>
            <span className={`badge ${plano === "gratis" ? "plain" : "accent"} account-dropdown-plan`}>
              Plano {getPlan(plano).nome}
            </span>
          </div>
          <button role="menuitem" onClick={() => go({ page: "account", tab: "plano" })}>
            <IconCard size={16} /> Plano e pagamento
          </button>
          <button role="menuitem" onClick={() => go({ page: "account", tab: "monitoramento" })}>
            <IconBell size={16} /> Empresas monitoradas
          </button>
          <button role="menuitem" onClick={() => go({ page: "batch" })}>
            <IconTable size={16} /> Consulta em lote
            {limites.loteDia === 0 && <span className="account-dropdown-tag">Pro</span>}
          </button>
          <button role="menuitem" onClick={() => go({ page: "account", tab: "chaves" })}>
            <IconKey size={16} /> Chaves de API
          </button>
          <button role="menuitem" onClick={() => go({ page: "account", tab: "seguranca" })}>
            <IconShield size={16} /> Segurança
          </button>
          <div className="account-dropdown-sep" role="separator" />
          <button
            role="menuitem"
            onClick={async () => {
              setOpen(false);
              await logout().catch(() => undefined);
              navigate({ page: "home" });
            }}
          >
            <IconLogout size={16} /> Sair
          </button>
        </div>
      )}
    </div>
  );
}
